import React, { useEffect, useState } from "react";
import { Lean, Verdict, VerdictOption, leanHeadline } from "../utils/verdict";

/** Starts fills at zero and grows them on the next frame (CSS skips it for reduced motion). */
function useGrow(): boolean {
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return grown;
}

function summary(verdict: Verdict): string {
  const shares = verdict.options.map((o) => `${o.label} ${o.pct}%`).join(", ");
  const leader = verdict.options.find((o) => o.key === verdict.winner);
  return `Panel verdict: ${shares}. ${leader ? `${leader.label} leads.` : "Even split."}`;
}

function caption(verdict: Verdict): string {
  const counted = verdict.experts.length;
  const who =
    counted < verdict.panelSize
      ? `${counted} of ${verdict.panelSize} experts'`
      : `${counted} experts'`;
  let moved = "";
  if (verdict.opening) {
    if (verdict.type === "yes_no") {
      const yes = verdict.opening.find((o) => o.key === "yes");
      if (yes) moved = ` · opened at ${yes.pct}% yes`;
    } else {
      const top = [...verdict.opening].sort((a, b) => b.pct - a.pct)[0];
      if (top) moved = ` · opening leader ${top.label} ${top.pct}%`;
    }
  }
  return `Average of ${who} final leans${moved}`;
}

const FILL_MOTION = "motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out";

function Pill({
  option,
  side,
  winner,
  grown,
  compact,
}: {
  option: VerdictOption;
  side: "left" | "right";
  winner: boolean;
  grown: boolean;
  compact: boolean;
}) {
  const yes = option.key === "yes";
  return (
    <div
      className={`relative ${compact ? "h-8" : "h-10"} flex-1 overflow-hidden ${side === "left" ? "rounded-l-full" : "rounded-r-full"} ${
        yes ? "bg-verdict-yes-track" : "bg-verdict-no-track"
      }`}
    >
      <span
        className={`absolute inset-y-0 ${side === "left" ? "right-0" : "left-0"} ${yes ? "bg-verdict-yes" : "bg-verdict-no"} ${FILL_MOTION}`}
        style={{ width: `${grown ? option.pct : 0}%` }}
      />
      <span className="relative flex h-full items-center justify-center font-mono text-sm font-semibold tabular-nums text-content">
        {option.pct}%
      </span>
      <span
        className={`pointer-events-none absolute inset-0 rounded-[inherit] ${
          winner ? "ring-2 ring-inset ring-verdict-win" : "ring-1 ring-inset ring-line"
        }`}
      />
    </div>
  );
}

function YesNoMeter({ verdict, grown, compact }: { verdict: Verdict; grown: boolean; compact: boolean }) {
  const yes = verdict.options.find((o) => o.key === "yes");
  const no = verdict.options.find((o) => o.key === "no");
  if (!yes || !no) return null;
  return (
    <div aria-hidden="true">
      <div className="flex gap-px">
        <Pill option={no} side="left" winner={verdict.winner === "no"} grown={grown} compact={compact} />
        <Pill option={yes} side="right" winner={verdict.winner === "yes"} grown={grown} compact={compact} />
      </div>
      <div className={`mt-1.5 flex ${compact ? "text-xs" : "text-sm"}`}>
        <span className={`flex-1 text-center text-verdict-no-text ${verdict.winner === "no" ? "font-semibold" : "font-medium"}`}>No</span>
        <span className={`flex-1 text-center text-verdict-yes-text ${verdict.winner === "yes" ? "font-semibold" : "font-medium"}`}>Yes</span>
      </div>
    </div>
  );
}

function ChoiceBreakdown({ verdict, grown }: { verdict: Verdict; grown: boolean }) {
  const ranked = [...verdict.options].sort((a, b) => b.pct - a.pct);
  return (
    <ul aria-hidden="true" className="space-y-1.5">
      {ranked.map((o) => {
        const winner = verdict.winner === o.key;
        return (
          <li key={o.key} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_3rem] items-center gap-3">
            <span className={`truncate text-sm text-content ${winner ? "font-semibold" : ""}`} title={o.label}>
              {o.label}
            </span>
            <span className="relative h-7 overflow-hidden rounded-md bg-raised">
              <span className={`absolute inset-y-0 left-0 bg-verdict-choice ${FILL_MOTION}`} style={{ width: `${grown ? o.pct : 0}%` }} />
              <span
                className={`pointer-events-none absolute inset-0 rounded-[inherit] ${
                  winner ? "ring-2 ring-inset ring-verdict-win" : "ring-1 ring-inset ring-line"
                }`}
              />
            </span>
            <span className="text-right font-mono text-sm font-semibold tabular-nums text-content">{o.pct}%</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Where the panel landed: two touching pills for yes/no (No left, Yes right,
 * each filling outward from the seam to its share), or one bar per option.
 * The leading answer gets a gold edge.
 */
export default function VerdictMeter({ verdict, compact = false }: { verdict: Verdict; compact?: boolean }) {
  const grown = useGrow();
  return (
    <figure className="m-0">
      <figcaption className="sr-only">{summary(verdict)}</figcaption>
      {verdict.type === "yes_no" ? (
        <YesNoMeter verdict={verdict} grown={grown} compact={compact} />
      ) : (
        <ChoiceBreakdown verdict={verdict} grown={grown} />
      )}
      {!compact && (
        <p className="mt-2 text-xs text-muted" aria-hidden="true">
          {verdict.winner ? "" : "Even split · "}
          {caption(verdict)}
        </p>
      )}
    </figure>
  );
}

/** One expert's lean, e.g. "Leans yes · 72%", with where it started when that differs. */
export function ExpertLeanTag({
  lean,
  opening,
  options,
}: {
  lean: Lean;
  opening?: Lean | null;
  options: Array<{ key: string; label: string }>;
}) {
  const headline = leanHeadline(lean, options);
  if (!headline) return null;
  const yesNo = options.length === 2 && options[0].key === "yes";
  const tone = yesNo ? (lean.yes > lean.no ? "text-verdict-yes-text" : lean.no > lean.yes ? "text-verdict-no-text" : "text-muted") : "text-content";
  const before = opening && JSON.stringify(opening) !== JSON.stringify(lean) ? leanHeadline(opening, options) : null;
  return (
    <span className="thread-tag gap-1 font-mono tabular-nums">
      <span className={tone}>{headline}</span>
      {before && <span className="text-muted">· opened {before.replace(/^Leans /, "")}</span>}
    </span>
  );
}
